import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ChatbotSettings.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ChatbotPreview from './ChatbotPreview';
import { FaPlus, FaEdit, FaTrash, FaCopy, FaRobot, FaSpinner } from 'react-icons/fa';

const ChatbotSettings = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [chatbotsList, setChatbotsList] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedChatbot, setSelectedChatbot] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [currentSettings, setCurrentSettings] = useState({
    primary_color: '#6200ea',
    secondary_color: '#03dac6',
    tertiary_color: '#bb86fc',
    company_name: 'Your Company',
    agent_name: 'ChatBot',
    greetings_message: 'Hello! How can I assist you today?',
    brand_id: null
  });

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/');
      return;
    }
    fetchInitialData(token);
  }, [navigate]);

  const fetchInitialData = async (token) => {
    try {
      setIsLoading(true);
      const userData = await fetchUserData(token);
      await fetchChatbotSettings(token);
      
      if (userData?.has_brand) {
        await fetchBrands(token);
      }
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
      setError('Failed to load chatbot settings');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserData = async (token) => {
    try {
      const response = await axios.get('/api/users/user', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      return null;
    }
  };

  const fetchChatbotSettings = async (token) => {
    try {
      const response = await axios.get('/api/chatbot/settings', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setChatbotsList(response.data || []);
      // Set the first chatbot as selected by default
      if (response.data && response.data.length > 0) {
        setSelectedChatbot(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch chatbot settings:', error);
      setChatbotsList([]);
    }
  };

  const fetchBrands = async (token) => {
    try {
      const response = await axios.get('/api/brands/list', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrands(response.data || []);
    } catch (error) {
      console.error('Failed to fetch brands:', error);
      setBrands([]);
    }
  };

  const handleCreateChatbot = () => {
    setModalMode('create');
    setCurrentSettings({
      primary_color: '#6200ea',
      secondary_color: '#03dac6',
      tertiary_color: '#bb86fc',
      company_name: 'Your Company',
      agent_name: 'ChatBot',
      greetings_message: 'Hello! How can I assist you today?',
      brand_id: null
    });
    setError(null);
    setSuccess(null);
    setIsModalOpen(true);
  };

  const handleEditChatbot = (chatbot) => {
    setModalMode('edit');
    setCurrentSettings({
      primary_color: chatbot.primary_color,
      secondary_color: chatbot.secondary_color,
      tertiary_color: chatbot.tertiary_color,
      company_name: chatbot.company_name,
      agent_name: chatbot.agent_name,
      greetings_message: chatbot.greetings_message,
      brand_id: chatbot.brand_id
    });
    setError(null);
    setSuccess(null);
    setIsModalOpen(true);
  };

  const handleDeleteChatbot = async (chatbotId) => {
    if (!window.confirm(t('chatbotSettings.confirmDelete', 'Are you sure you want to delete this chatbot?'))) {
      return;
    }

    const token = localStorage.getItem('access_token');
    try {
      await axios.delete(`/api/chatbot/settings/${chatbotId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await fetchChatbotSettings(token);
      
      // Reset selected chatbot if it was deleted
      if (selectedChatbot && selectedChatbot.id === chatbotId) {
        setSelectedChatbot(chatbotsList.length > 1 ? chatbotsList.find(c => c.id !== chatbotId) : null);
      }
      
      setSuccess(t('chatbotSettings.deleteSuccess', 'Chatbot deleted successfully'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to delete chatbot:', error);
      setError(t('chatbotSettings.deleteError', 'Failed to delete chatbot'));
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleChange = (e) => {
    setCurrentSettings({ ...currentSettings, [e.target.name]: e.target.value });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    const token = localStorage.getItem('access_token');
    
    try {
      if (modalMode === 'create') {
        const response = await axios.post('/api/chatbot/settings', currentSettings, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess(t('chatbotSettings.createSuccess', 'Chatbot created successfully'));
        // Set the newly created chatbot as selected
        if (response.data) {
          setSelectedChatbot(response.data);
        }
      } else {
        const response = await axios.put(`/api/chatbot/settings/${selectedChatbot.id}`, currentSettings, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess(t('chatbotSettings.updateSuccess', 'Chatbot updated successfully'));
        // Update the selected chatbot with new data
        if (response.data) {
          setSelectedChatbot(response.data);
        }
      }
      
      await fetchChatbotSettings(token);
      
      setTimeout(() => {
        setIsModalOpen(false);
        setSuccess(null);
      }, 2000);
    } catch (error) {
      console.error('Failed to save chatbot settings:', error);
      setError(error.response?.data?.detail || t('chatbotSettings.saveError', 'Failed to save chatbot settings'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateEmbedCode = async (chatbotId, brandId = null) => {
    try {
      const token = localStorage.getItem('access_token');
      const params = brandId ? `?brand_id=${brandId}` : '';
      const response = await axios.get(`/api/chatbot/embed${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.embed_code;
    } catch (error) {
      console.error('Failed to generate embed code:', error);
      return '';
    }
  };

  const handleCopyEmbedCode = async (chatbot) => {
    const embedCode = await generateEmbedCode(chatbot.id, chatbot.brand_id);
    if (embedCode) {
      navigator.clipboard.writeText(embedCode);
      setSuccess(t('chatbotSettings.copySuccess', 'Embed code copied to clipboard'));
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const getBrandName = (brandId) => {
    if (!brandId) return t('chatbotSettings.defaultChatbot', 'Default Chatbot');
    const brand = brands.find(b => b.id === brandId);
    return brand ? brand.name : t('chatbotSettings.unknownBrand', 'Unknown Brand');
  };

  if (isLoading) {
    return (
      <div className="chatbot-settings-loading">
        <FaSpinner className="spinner" />
        <h2>{t('chatbotSettings.loading', 'Loading chatbot settings...')}</h2>
      </div>
    );
  }

  // For users without brands, show the simplified single chatbot interface
  if (!user?.has_brand) {
    const singleChatbot = chatbotsList[0];
    
    return (
      <div className="chatbot-settings-container">
        <h2 className="settings-title">{t('chatbotSettings.title')}</h2>

        <div className="chatbot-settings-flex">
          <div className="chatbot-settings-form">
            <form onSubmit={(e) => {
              e.preventDefault();
              if (singleChatbot) {
                setSelectedChatbot(singleChatbot);
                setCurrentSettings(singleChatbot);
                setModalMode('edit');
                setIsModalOpen(true);
              }
            }}>
              {singleChatbot && (
                <>
                  <div className="form-group">
                    <label>{t('chatbotSettings.companyName')}</label>
                    <input type="text" value={singleChatbot.company_name} readOnly />
                  </div>
                  <div className="form-group">
                    <label>{t('chatbotSettings.aiAgentName')}</label>
                    <input type="text" value={singleChatbot.agent_name} readOnly />
                  </div>
                  <div className="form-group">
                    <label>{t('chatbotSettings.greetingMessage')}</label>
                    <textarea value={singleChatbot.greetings_message} readOnly rows="3" />
                  </div>
                  <div className="form-group color-group">
                    <label>{t('chatbotSettings.primaryColor')}</label>
                    <input type="color" value={singleChatbot.primary_color} readOnly />
                  </div>
                  <div className="form-group color-group">
                    <label>{t('chatbotSettings.secondaryColor')}</label>
                    <input type="color" value={singleChatbot.secondary_color} readOnly />
                  </div>
                  <div className="form-group color-group">
                    <label>{t('chatbotSettings.tertiaryColor')}</label>
                    <input type="color" value={singleChatbot.tertiary_color} readOnly />
                  </div>
                  <button type="submit" className="save-button">
                    <FaEdit /> {t('chatbotSettings.edit', 'Edit Settings')}
                  </button>
                </>
              )}
            </form>

            {singleChatbot && (
              <div className="embed-code-section">
                <h3>{t('chatbotSettings.embedCodeTitle')}</h3>
                <button
                  className="copy-embed-button"
                  onClick={() => handleCopyEmbedCode(singleChatbot)}
                >
                  <FaCopy /> {t('chatbotSettings.copyEmbedCode')}
                </button>
              </div>
            )}
          </div>

          {singleChatbot && (
            <div className="chatbot-preview-section">
              <h3 className="preview-title">{t('chatbotSettings.previewTitle')}</h3>
              <ChatbotPreview
                primary_color={singleChatbot.primary_color}
                secondary_color={singleChatbot.secondary_color}
                tertiary_color={singleChatbot.tertiary_color}
                company_name={singleChatbot.company_name}
                agent_name={singleChatbot.agent_name}
                greetings_message={singleChatbot.greetings_message}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // For users with brands, show the multi-chatbot interface
  return (
    <div className="chatbot-settings-container">
      <div className="chatbot-settings-header">
      <h2 className="settings-title">{t('chatbotSettings.title')}</h2>
        <button 
          className="create-chatbot-button"
          onClick={handleCreateChatbot}
        >
          <FaPlus /> {t('chatbotSettings.createChatbot', 'Create Chatbot')}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="chatbots-management">
        <div className="chatbots-list">
          <h3>{t('chatbotSettings.yourChatbots', 'Your Chatbots')}</h3>
          {chatbotsList.length === 0 ? (
            <div className="no-chatbots">
              <FaRobot className="no-chatbots-icon" />
              <p>{t('chatbotSettings.noChatbots', 'No chatbots found. Create your first chatbot!')}</p>
            </div>
          ) : (
            <div className="chatbots-grid">
              {chatbotsList.map(chatbot => (
                <div 
                  key={chatbot.id} 
                  className={`chatbot-card ${selectedChatbot?.id === chatbot.id ? 'selected' : ''}`}
                  onClick={() => setSelectedChatbot(chatbot)}
                >
                  <div className="chatbot-card-header">
                    <h4>{chatbot.company_name}</h4>
                    <div className="chatbot-actions">
                      <button
                        className="action-button edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditChatbot(chatbot);
                        }}
                        title={t('chatbotSettings.edit', 'Edit')}
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="action-button copy"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyEmbedCode(chatbot);
                        }}
                        title={t('chatbotSettings.copyEmbedCode', 'Copy Embed Code')}
                      >
                        <FaCopy />
                      </button>
                      <button
                        className="action-button delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteChatbot(chatbot.id);
                        }}
                        title={t('chatbotSettings.delete', 'Delete')}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                  <div className="chatbot-card-content">
                    <p><strong>{t('chatbotSettings.brand')}:</strong> {getBrandName(chatbot.brand_id)}</p>
                    <p><strong>{t('chatbotSettings.agent')}:</strong> {chatbot.agent_name}</p>
                    <div className="color-preview">
                      <div className="color-circle" style={{ backgroundColor: chatbot.primary_color }}></div>
                      <div className="color-circle" style={{ backgroundColor: chatbot.secondary_color }}></div>
                      <div className="color-circle" style={{ backgroundColor: chatbot.tertiary_color }}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedChatbot && (
          <div className="chatbot-preview-section">
            <h3 className="preview-title">{t('chatbotSettings.previewTitle')}</h3>
            <ChatbotPreview
              primary_color={selectedChatbot.primary_color}
              secondary_color={selectedChatbot.secondary_color}
              tertiary_color={selectedChatbot.tertiary_color}
              company_name={selectedChatbot.company_name}
              agent_name={selectedChatbot.agent_name}
              greetings_message={selectedChatbot.greetings_message}
            />
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>
              {modalMode === 'create' 
                ? t('chatbotSettings.createChatbot', 'Create Chatbot')
                : t('chatbotSettings.editChatbot', 'Edit Chatbot')
              }
            </h2>
            
            <form onSubmit={handleSave}>
              {user?.has_brand && (
                <div className="form-group">
                  <label htmlFor="brand_id">{t('chatbotSettings.brand')}</label>
                  <select
                    id="brand_id"
                    name="brand_id"
                    value={currentSettings.brand_id || ''}
                    onChange={handleChange}
                  >
                    <option value="">{t('chatbotSettings.defaultChatbot', 'Default Chatbot')}</option>
                    {brands.map(brand => (
                      <option key={brand.id} value={brand.id}>{brand.name}</option>
                    ))}
                  </select>
                </div>
              )}

            <div className="form-group">
              <label htmlFor="company_name">{t('chatbotSettings.companyName')}</label>
              <input
                type="text"
                id="company_name"
                name="company_name"
                  value={currentSettings.company_name}
                onChange={handleChange}
                placeholder="Enter your company name"
                  required
              />
            </div>

            <div className="form-group">
              <label htmlFor="agent_name">{t('chatbotSettings.aiAgentName')}</label>
              <input
                type="text"
                id="agent_name"
                name="agent_name"
                  value={currentSettings.agent_name}
                onChange={handleChange}
                placeholder="Enter chatbot name"
                  required
              />
            </div>

            <div className="form-group">
              <label htmlFor="greetings_message">{t('chatbotSettings.greetingMessage')}</label>
              <textarea
                id="greetings_message"
                name="greetings_message"
                  value={currentSettings.greetings_message}
                onChange={handleChange}
                placeholder="Enter greeting message"
                rows="3"
                  required
              ></textarea>
            </div>

            <div className="form-group color-group">
              <label htmlFor="primary_color">{t('chatbotSettings.primaryColor')}</label>
              <input
                type="color"
                id="primary_color"
                name="primary_color"
                  value={currentSettings.primary_color}
                onChange={handleChange}
              />
            </div>

            <div className="form-group color-group">
              <label htmlFor="secondary_color">{t('chatbotSettings.secondaryColor')}</label>
              <input
                type="color"
                id="secondary_color"
                name="secondary_color"
                  value={currentSettings.secondary_color}
                onChange={handleChange}
              />
            </div>

            <div className="form-group color-group">
              <label htmlFor="tertiary_color">{t('chatbotSettings.tertiaryColor')}</label>
              <input
                type="color"
                id="tertiary_color"
                name="tertiary_color"
                  value={currentSettings.tertiary_color}
                onChange={handleChange}
              />
            </div>

              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}

              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={() => setIsModalOpen(false)}
                  className="cancel-button"
                >
                  {t('chatbotSettings.cancel', 'Cancel')}
            </button>
            <button
                  type="submit" 
                  className="save-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <FaSpinner className="spinner" />
                      {t('chatbotSettings.saving', 'Saving...')}
                    </>
                  ) : (
                    modalMode === 'create' 
                      ? t('chatbotSettings.create', 'Create')
                      : t('chatbotSettings.save', 'Save')
                  )}
            </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotSettings;
